import React, { useMemo, lazy, Suspense } from 'react'
import { css } from '@emotion/core'
import { H1, H2, useContainerWidth, Separator, LayoutBand, Card, Grid, Button, useOverlay, Alert } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { useGroupsContext } from '@fs/zion-tree-groups'
import { useFeatureFlag } from '@fs/zion-flags'
import { usePermission } from '@fs/zion-permissions'
import AllTreesData from '../tree-import/components/cets/AllTreesData'
import UploadGedcomCard from '../tree-import/components/manage-trees/UploadGedcomCard'
import { sortAndFilterGroups } from '../services/getGroupsList'
import ManageTreeGroup from '../tree-import/components/manage-trees/ManageTreeGroup'
import CenteredLoadingSpinner from '../components/CenteredLoadingSpinner'
import GenealogyMobileTable from '../tree-import/components/genealogy-trees/genealogy-table/GenealogyMobileTable'
import GenealogyTable from '../tree-import/components/genealogy-trees/genealogy-table/GenealogyTable'
import GenealogyTreesProvider, {
  useGenealogyTreesContext,
} from '../tree-import/components/genealogy-trees/GenealogyTreesProvider'
import { useTreeUpload } from '../tree-import/hooks/useTreeUpload'

const pageContainerCss = css`
  min-height: calc(100vh - var(--header-height, 0px));
`

const UploadDialog = lazy(() => import('../tree-import/components/upload-page/upload-dialog/UploadDialog'))

function ManageTreesContent({ isMobile }) {
  const { t } = useTranslation()
  const { groups, canAccessCET, canAccessFGT } = useGroupsContext()
  const researcherTreesOn = useFeatureFlag('treeWeb_researcherTrees').isOn
  const [isMember] = usePermission('FtMemberUiPermission')

  const { sortedFamilyGroups, ownedTrees, sharedTrees } = useMemo(() => {
    const researchGroups = sortAndFilterGroups({
      groups: groups || [],
    })
    const familyGroups = sortAndFilterGroups({
      groups: groups || [],
      isFamily: true,
    })

    const { owned, shared } = researchGroups.reduce(
      (acc, group) => {
        if (group.memberStatus === 'ADMIN') {
          acc.owned.push(group)
        } else if (group.memberStatus === 'MEMBER') {
          acc.shared.push(group)
        }
        return acc
      },
      { owned: [], shared: [] }
    )

    return {
      sortedFamilyGroups: familyGroups,
      ownedTrees: owned,
      sharedTrees: shared,
    }
  }, [groups])

  // Show upload card if user has researcher trees flag OR is a member, AND has no owned trees
  const showUploadGedcomCard = (researcherTreesOn || isMember) && canAccessCET && !ownedTrees?.length

  // Show Trees You Own section if user has CET access OR owns trees
  const showTreesYouOwn = canAccessCET || ownedTrees?.length > 0

  return (
    <>
      {/* 1. Groups With Trees - Show if user has FGT groups */}
      {canAccessFGT && sortedFamilyGroups.length > 0 && (
        <>
          <ManageTreeGroup
            groupTitle={t('tree-import.manage-trees.family-groups-heading')}
            groups={sortedFamilyGroups}
            isMobile={isMobile}
          />
          <Separator size="xs" />
        </>
      )}

      {/* 2. Trees You Own - Show upload card or owned trees */}
      {showTreesYouOwn && showUploadGedcomCard && (
        <>
          <UploadGedcomCard />
          <Separator size="xs" />
        </>
      )}

      {showTreesYouOwn && !showUploadGedcomCard && ownedTrees.length > 0 && (
        <>
          <ManageTreeGroup
            groupTitle={t('tree-import.manage-trees.owned-groups-heading')}
            groups={ownedTrees}
            isMobile={isMobile}
            isOwnedTreesGroup
          />
          <Separator size="xs" />
        </>
      )}

      {/* 3. Trees Shared With You - Show if user has shared trees */}
      {sharedTrees.length > 0 && (
        <>
          <ManageTreeGroup
            groupTitle={t('tree-import.manage-trees.shared-groups-heading')}
            groups={sharedTrees}
            isMobile={isMobile}
          />
          <Separator size="xs" />
        </>
      )}
    </>
  )
}

function GenealogyTreesCard({ isMobile }) {
  const { t } = useTranslation()
  const { trees, error: genealogyError, refetch } = useGenealogyTreesContext()
  const uploadOverlay = useOverlay()

  const {
    collections,
    uploading,
    error: uploadError,
    handleUpload,
  } = useTreeUpload({
    fetchCollectionsOnMount: true,
    onUploadSuccess: refetch,
  })

  const hasCollections = collections && collections.length > 0

  return (
    <Card margins="xs">
      <Grid guttersY="sm">
        <H2 size="H5">{t('tree-import.manage-trees.genealogy-trees-heading')}</H2>
        {genealogyError && (
          <Alert dense type="error">
            {t('tree-import.upload-trees.error.503.web_service_down_error')}
          </Alert>
        )}
        {trees && trees.length > 0 && (isMobile ? <GenealogyMobileTable /> : <GenealogyTable />)}
        {(!trees || trees.length === 0) && !genealogyError && (
          <Alert dense type="help">
            {t('tree-import.manage-trees.no-genealogy-trees')}
          </Alert>
        )}

        {/* Upload button below the table */}
        {hasCollections && (
          <>
            <Separator size="xs" />
            <Button
              color="blue"
              emphasis="medium"
              onClick={uploadOverlay.handleClick}
              linkName="Genealogy Upload Button"
              data-testid="genealogy-upload-button"
            >
              {t('tree-import.button.upload-gedcom-file')}
            </Button>

            <Suspense fallback="">
              {hasCollections && (
                <UploadDialog
                  overlay={uploadOverlay}
                  handleUpload={(uploadData) => handleUpload(uploadData, uploadOverlay)}
                  collections={collections}
                  uploading={uploading}
                  showAllCollections
                />
              )}
            </Suspense>

            {uploadError && (
              <Alert dense type="error">
                {uploadError}
              </Alert>
            )}
          </>
        )}
      </Grid>
    </Card>
  )
}

export default function ManageTreesPage() {
  const { t } = useTranslation()
  const atWidth = useContainerWidth()
  const isMobile = atWidth({ default: true, lg: false })
  const { groups, loading } = useGroupsContext()

  if (loading || !groups) {
    return <CenteredLoadingSpinner />
  }

  return (
    <div css={pageContainerCss}>
      <LayoutBand marginY="md">
        <H1 size="H4">{t('tree-import.manage-trees.heading')}</H1>
        <Separator size="xs" />
        <AllTreesData>
          <ManageTreesContent isMobile={isMobile} />
        </AllTreesData>

        {/* 4. Uploaded to Genealogies - Always show with upload button */}
        <GenealogyTreesProvider>
          <GenealogyTreesCard isMobile={isMobile} />
        </GenealogyTreesProvider>
      </LayoutBand>
    </div>
  )
}
